import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertCircle, ChevronRight, ListChecks, Package, Plus } from 'lucide-react'

import { Button, EmptyState } from '../../radix'
import { ListNameDialog } from './ListNameDialog'
import { useFavouriteLists } from '../../../auth/hooks/useFavouriteLists'
import { useFavourites } from '../../../auth/hooks/useFavourites'
import {
  FROM_LISTS_STATE,
  favouriteListPath,
  itemCountLabel,
  saveListName,
} from '../../../utils/favouriteLists'
import type { Favourite, FavouriteList } from '../../../types'

const MAX_THUMBNAILS = 3

/**
 * The "Lists" tab on Favourites (DEV-51010 AC8, AC9): a "New list" button,
 * then one card per list, most recently changed first. With no lists it
 * shows an empty state explaining what lists are for.
 */
export function ListsPanel() {
  const { lists, isLoading, isError, refetch, createList } = useFavouriteLists()
  const {
    favourites,
    isLoading: favouritesLoading,
    isError: favouritesError,
    refetch: refetchFavourites,
  } = useFavourites()
  const [creating, setCreating] = useState(false)

  // Cards show the same resolved image as the All favourites rows.
  const favouritesById = useMemo(
    () => new Map(favourites.map((favourite) => [favourite.id, favourite])),
    [favourites],
  )

  const newListDialog = (
    <ListNameDialog
      open={creating}
      onOpenChange={setCreating}
      title="New list"
      submitLabel="Create list"
      onSubmit={async (name) => {
        const result = await saveListName(name, lists, (value) => createList(value))
        return result.ok ? null : result.error
      }}
    />
  )

  if (isLoading || favouritesLoading) return <ListsSkeleton />

  // Counts and thumbnails come from the favourites query, so if it failed
  // every card would wrongly read "0 items".
  if (isError || favouritesError) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-10 w-10 text-gray-400" aria-hidden="true" />}
        title="Couldn't load your lists"
        description="Check your connection and try again."
        action={
          <Button
            onClick={() => {
              if (isError) refetch()
              if (favouritesError) refetchFavourites()
            }}
          >
            Try again
          </Button>
        }
      />
    )
  }

  if (lists.length === 0) {
    return (
      <>
        <EmptyState
          icon={<ListChecks className="h-10 w-10 text-brand-primary" aria-hidden="true" />}
          title="No lists yet"
          description="Lists group your favourites by job."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              New list
            </Button>
          }
        />
        {newListDialog}
      </>
    )
  }

  return (
    <div>
      <Button className="mb-4 w-full" onClick={() => setCreating(true)}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        New list
      </Button>
      <ul className="space-y-2">
        {lists.map((list) => (
          <li key={list.id}>
            <ListCard list={list} favouritesById={favouritesById} />
          </li>
        ))}
      </ul>
      {newListDialog}
    </div>
  )
}

function ListCard({
  list,
  favouritesById,
}: {
  list: FavouriteList
  favouritesById: ReadonlyMap<string, Favourite>
}) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const items = list.favouriteIds
    .map((id) => favouritesById.get(id))
    .filter((favourite): favourite is Favourite => favourite !== undefined)
  // favouriteIds is newest first, so these are the latest additions.
  const thumbnails = items.slice(0, MAX_THUMBNAILS)

  return (
    <Link
      to={favouriteListPath(tenantSlug, list.id)}
      state={FROM_LISTS_STATE}
      className="flex min-h-[72px] items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 transition-colors hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-gray-900">{list.name}</span>
        <span className="mt-0.5 block text-sm text-gray-500">{itemCountLabel(items.length)}</span>
      </span>
      {thumbnails.length > 0 && (
        // Overlapping stack; the white border separates each image.
        <span className="flex flex-shrink-0 -space-x-2" aria-hidden="true">
          {thumbnails.map((favourite) => (
            <span
              key={favourite.id}
              className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border-2 border-white bg-gray-100"
            >
              {favourite.listing?.imageUrl ? (
                <img
                  src={favourite.listing.imageUrl}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : (
                <Package className="h-4 w-4 text-gray-300" />
              )}
            </span>
          ))}
        </span>
      )}
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300" aria-hidden="true" />
    </Link>
  )
}

export function ListsSkeleton() {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading lists">
      {[0, 1, 2].map((row) => (
        <div key={row} className="h-[72px] rounded-xl bg-gray-100 motion-safe:animate-pulse" />
      ))}
    </div>
  )
}
